import type { PaymentSelection, WalletBrand } from "@utils/store/cart/useOrder";
import type { OwnedPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";

/** 電子支付品牌清單（付款方式步驟的品牌鈕，順序即顯示順序；Apple Pay 只在 iPhone 出現）。 */
export const WALLET_BRANDS: { id: WalletBrand; label: string }[] = [
  { id: "apple_pay", label: "Apple Pay" },
  { id: "line_pay", label: "LINE Pay" },
  { id: "ipass_money", label: "一卡通 MONEY" },
];

/**
 * 付款方式選擇 → 送單 payment_method_ref。
 * 後端（app-api platform-orders.service）接受：已綁卡 id、'apple_pay' / 'line_pay' 字面量、'cash_on_delivery'。
 * 未選（null）回 null，由呼叫端決定要擋還是退回貨到付款。
 */
export const paymentSelectionToRef = (sel: PaymentSelection | null): string | null => {
  if (!sel) return null;
  switch (sel.type) {
    case "credit_card":
      return sel.paymentMethodId;
    case "electronic":
      return sel.brand;
    default:
      return "cash_on_delivery";
  }
};

/** 結帳頁「支付方式」列的顯示文字；未選回 null（呼叫端顯示「請選擇」）。 */
export const paymentSelectionLabel = (sel: PaymentSelection | null, monthly: boolean): string | null => {
  if (!sel) return null;
  switch (sel.type) {
    case "credit_card":
    case "electronic":
      return sel.label;
    case "monthly":
      return "月結";
    default:
      return monthly ? "月結" : "貨到付款";
  }
};

export type PayCatId = "card" | "wallet" | "cash";

export const catFromSelection = (s: PaymentSelection | null): PayCatId | null => {
  if (!s) return null;
  if (s.type === "credit_card") return "card";
  if (s.type === "electronic") return "wallet";
  return "cash";
};

export const cardSelectionLabel = (c: OwnedPaymentMethod) => `信用卡 •••• ${c.card?.lastfour ?? "----"}`;

const LAST_PAYMENT_KEY = "gasShop.lastPayment";
/** 舊版只記類別；沒有新紀錄時拿來推預設類別，之後自然被新紀錄取代。 */
const LEGACY_LAST_CAT_KEY = "gasShop.lastPaymentCat";

const isPaymentSelection = (v: unknown): v is PaymentSelection => {
  if (!v || typeof v !== "object") return false;
  const s = v as Record<string, unknown>;
  if (s.type === "cash_on_delivery" || s.type === "monthly") return true;
  if (s.type === "credit_card") return typeof s.paymentMethodId === "string" && !!s.paymentMethodId && typeof s.label === "string";
  if (s.type === "electronic") return WALLET_BRANDS.some((b) => b.id === s.brand) && typeof s.label === "string";
  return false;
};

/** 上次按「前往確認訂單」時的付款方式（存在這台裝置）；沒有或格式不對回 null。 */
export const readLastPayment = (): PaymentSelection | null => {
  try {
    const raw = localStorage.getItem(LAST_PAYMENT_KEY);
    if (!raw) return null;
    const v: unknown = JSON.parse(raw);
    return isPaymentSelection(v) ? v : null;
  } catch {
    return null;
  }
};

export const writeLastPayment = (s: PaymentSelection) => {
  try {
    localStorage.setItem(LAST_PAYMENT_KEY, JSON.stringify(s));
  } catch {
    /* 無痕模式等寫不進去：下次就沒有預設，不影響下單 */
  }
};

const readLegacyLastCat = (): PayCatId | null => {
  try {
    const v = localStorage.getItem(LEGACY_LAST_CAT_KEY);
    if (v === "card" || v === "wallet" || v === "cash") return v;
  } catch {
    /* ignore */
  }
  return null;
};

/** 進頁時先打開哪一類：上次付款的類別 → 舊版記的類別 → 信用卡。 */
export const readLastPayCat = (): PayCatId => catFromSelection(readLastPayment()) ?? readLegacyLastCat() ?? "card";

/**
 * 進付款方式頁、還沒選過時的預設：沿用上次的付款方式。
 * - 上次的卡已刪除／失效 → 改帶帳戶預設卡（沒有就第一張）
 * - 上次用 Apple Pay 但這台不是 iPhone → 不預選，讓客戶自己挑
 * - 沒有新紀錄 → 依舊版記的類別（現金／卡片）推
 */
export const pickDefaultPayment = ({
  last,
  cards,
  defaultCardId,
  showApplePay,
  isMonthly,
}: {
  last: PaymentSelection | null;
  cards: OwnedPaymentMethod[];
  defaultCardId: string | null;
  showApplePay: boolean;
  isMonthly: boolean;
}): PaymentSelection | null => {
  const cash: PaymentSelection = isMonthly ? { type: "monthly" } : { type: "cash_on_delivery" };
  const preferredCard = (): PaymentSelection | null => {
    const c = (defaultCardId && cards.find((x) => x.payment_method_id === defaultCardId)) || cards[0];
    return c ? { type: "credit_card", paymentMethodId: c.payment_method_id, label: cardSelectionLabel(c) } : null;
  };

  if (last) {
    if (last.type === "credit_card") {
      const hit = cards.find((c) => c.payment_method_id === last.paymentMethodId);
      return hit ? { type: "credit_card", paymentMethodId: hit.payment_method_id, label: cardSelectionLabel(hit) } : preferredCard();
    }
    if (last.type === "electronic") {
      if (last.brand === "apple_pay" && !showApplePay) return null;
      const brand = WALLET_BRANDS.find((b) => b.id === last.brand);
      return brand ? { type: "electronic", brand: brand.id, label: brand.label } : null;
    }
    return cash;
  }

  const legacy = readLegacyLastCat();
  if (legacy === "cash") return cash;
  if (legacy === "card") return preferredCard();
  return null;
};
